import { TemplatesResult } from '@/service'
import Link from 'next/link'
import { RiArrowRightLine, RiFileListLine } from '@remixicon/react'
import { useState } from 'react'
import styles from './templates.module.css'

interface TemplateItemProps {
  heading: string
  headingLink?: string
  templates: TemplatesResult[]
}

export function TemplateCard({ template }: { template: TemplatesResult }) {
  const [imageFailed, setImageFailed] = useState(false)
  const thumb = template.Thumb?.[0]
  const thumbnail =
    thumb?.presignedUrl ||
    (thumb?.path?.startsWith('https://') ? thumb.path : '')
  return (
    <Link
      href={`/templates/${encodeURIComponent(template.Slug.toLowerCase())}`}
      className={styles.card}
    >
      {thumbnail && !imageFailed ? (
        <img
          className={styles.thumbnail}
          src={thumbnail}
          alt=""
          width={thumb.width || 640}
          height={thumb.height || 400}
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <RiFileListLine className={styles.cardIcon} aria-hidden="true" />
      )}
      <h3>{template.Title}</h3>
      {template.Intro && <p className={styles.cardIntro}>{template.Intro}</p>}
      <div className={styles.cardFooter}>
        <span>
          {template.Questions > 0
            ? `${template.Questions} ${template.Questions === 1 ? 'question' : 'questions'}`
            : template.Category?.[0] || 'Template'}
        </span>
        <span className={styles.cardAction}>
          View template
          <RiArrowRightLine aria-hidden="true" />
        </span>
      </div>
    </Link>
  )
}

export function TemplateItem({
  heading,
  headingLink,
  templates
}: TemplateItemProps): JSX.Element {
  return (
    <section className={styles.templateGroup}>
      <div className={styles.groupHeading}>
        <h2>{heading}</h2>
        {headingLink ? (
          <Link
            className={styles.viewAll}
            href={headingLink}
            aria-label={`View all ${heading}`}
          >
            View all
            <RiArrowRightLine aria-hidden="true" />
          </Link>
        ) : (
          <span className={styles.resultCount}>
            {templates.length}{' '}
            {templates.length === 1 ? 'template' : 'templates'}
          </span>
        )}
      </div>
      {templates.length ? (
        <div className={styles.cardGrid}>
          {templates.map(template => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          <h3>No templates found</h3>
          <p>
            Try a broader search, or browse another category to find a starting
            point.
          </p>
          <Link className={styles.textLink} href="/templates">
            Browse all templates
            <RiArrowRightLine aria-hidden="true" />
          </Link>
        </div>
      )}
    </section>
  )
}
